import { api, apiDataUrl, apiDownload } from './http';
import { usageHeaders } from './usage';
import { storedAttribution } from '../features/growth/attribution';
import { backendLanguage, type BackendLanguage } from '@smbsuite/i18n';
import { viewerZone } from '../time/viewer-zone';
import type { EnvelopeInput } from '@smbsuite/api/signing';
import type {
  AssistantSettings,
  MyAssistants,
  EditorCapabilities,
  ImportCheck,
  ImportEntity,
  ImportOptions,
  ImportProblem,
  GoogleContactsStatus,
  MicrosoftContactsStatus,
  ImportRecord,
  ImportUndoPreview,
  Marketplace,
  PersonSettings,
  SendingDomainView,
  DealSizes,
  Appearance,
  Account,
  ChartPeriod,
  PipelineSummary,
  AccountDetails,
  AccountRelation,
  ContactAccountLink,
  CommunicationCategory,
  ContactPreferencesView,
  ConsentMethod,
  CurrentPreference,
  PreferenceChangeEntry,
  PreferenceState,
  Activity,
  AttendeeInput,
  AppNotification,
  Attachment,
  AttachmentTarget,
  FileLink,
  FileLinks,
  PinType,
  Pins,
  RecentFile,
  RecordShare,
  ShareableType,
  AccountGroup,
  LookupResult,
  TaskAttachment,
  AuditEvent,
  Board,
  Catalog,
  Company,
  CompanySettings,
  Connection,
  DriveImport,
  DriveItem,
  DriveMirror,
  Contact,
  LastContacted,
  Tag,
  TagRecordType,
  TeamRecordType,
  TagRef,
  TagGrant,
  TagLevel,
  TagTable,
  CreatedLink,
  CustomValues,
  Deal,
  Envelope,
  ErasureRequest,
  ExportJob,
  FieldDefinition,
  FieldInput,
  FileDetails,
  FileItem,
  FileFind,
  FileFindPage,
  ItemTags,
  GmailLabel,
  MailLogAddress,
  MailLogSettings,
  CalendarSyncSettings,
  CalendarChoice,
  BusyTime,
  ItemTeams,
  Folder,
  Grant,
  Invitation,
  Link,
  Listing,
  FileTreeRecord,
  TreeFolder,
  MoveCheck,
  MoveChoice,
  MoveItemRef,
  MoveResult,
  Member,
  MemberRoster,
  MyInvitations,
  CompanyDetails,
  CompanyDetailsState,
  CompanyVatCheck,
  Page,
  Pipeline,
  Preference,
  Provider,
  ProviderId,
  PublicLink,
  RecordType,
  RelationType,
  DealLineSettings,
  DealLine,
  DealLineTotals,
  DealLineSuggestion,
  PlaceSearch,
  LineOption,
  Role,
  Rule,
  RuleAction,
  Run,
  Trigger,
  DashboardLayout,
  TaskCategory,
  TaskStatus,
  UserActivityType,
  EmailDirection,
  CallDirection,
  CallOutcome,
  TimelineCounts,
  SearchHit,
  SharedItem,
  SignedUrl,
  UploadIntent,
  Usage,
  VatState,
  BillingDetails,
  Entitlements,
  Invoice,
  Plan,
  Subscription,
  Team,
  TeamDetails,
  TeamJoining,
  TeamList,
  TeamMember,
  TeamNoticeChoice,
  TeamNoticeDefaults,
  TeamOutside,
  TeamOutsideChange,
  TeamRole,
} from './types';

/** A deal's line as sent (TF-251): an id for one it has; unit and VAT left out keep theirs (a new line: the defaults). */
export type DealLineInput = Pick<DealLine, 'name' | 'quantity' | 'priceCents'> & Partial<Pick<DealLine, 'id' | 'kind' | 'unitId' | 'discount' | 'vatId' | 'periodId' | 'periods'>>;

/** The deal lines lists as the API names them (TF-321). */
export type DealLineList = 'units' | 'periods' | 'vat-rates';
/** An entry as sent: an id for one the company has; a preset's name null keeps its translated one. */
export type LineOptionInput = Partial<Pick<LineOption, 'id' | 'name' | 'short' | 'every' | 'everyUnit' | 'isDefault' | 'active'>> & { rate?: number };

// One function per platform call, so pages never spell out URLs.

const q = (params: Record<string, string | number | undefined | null>) => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  const text = search.toString();
  return text ? `?${text}` : '';
};

export const tenantApi = {
  // My own settings, kept with my account (TF-220): the time zone.
  mySettings: () => api<PersonSettings>('/api/tenant/v1/me/settings'),
  saveMySettings: (change: Partial<PersonSettings>) => api<PersonSettings>('/api/tenant/v1/me/settings', { method: 'PUT', body: change }),
  // The dashboard's arrangement (TF-6): mine, and the company's default people start from.
  dashboard: (tenantId: string) => api<{ mine: DashboardLayout | null; company: DashboardLayout | null }>('/api/tenant/v1/dashboard', { tenantId }),
  saveDashboard: (tenantId: string, layout: DashboardLayout) => api<{ layout: DashboardLayout }>('/api/tenant/v1/dashboard', { method: 'PUT', tenantId, body: layout }),
  resetDashboard: (tenantId: string) => api<void>('/api/tenant/v1/dashboard', { method: 'DELETE', tenantId }),
  saveCompanyDashboard: (tenantId: string, layout: DashboardLayout) =>
    api<{ layout: DashboardLayout }>('/api/tenant/v1/dashboard/company', { method: 'PUT', tenantId, body: layout }),
  // Opening the app: the device and language go with it, counted per company and day (TF-127).
  companies: () => api<{ tenants: Company[] }>('/api/tenant/v1/tenants', { headers: usageHeaders() }).then((body) => body.tenants),
  /** Registering a company (TF-79): a name, and who it is; `noNumber` after the person confirmed there is none yet. */
  /** With where the person came from and the app's language, for the growth funnel (TF-126). */
  createCompany: (input: Partial<CompanyDetails> & { name: string; noNumber?: boolean; turnstileToken?: string; acceptTerms?: string }) =>
    api<Company>('/api/tenant/v1/tenants', {
      method: 'POST',
      body: { ...input, attribution: storedAttribution() ?? undefined, language: backendLanguage(document.documentElement.lang) },
    }),
  /** An owner accepts the terms of service in force for the company (TF-256). */
  acceptTerms: (tenantId: string, version: string) =>
    api<{ termsVersion: string }>('/api/tenant/v1/tenants/current/terms', { method: 'PUT', tenantId, body: { version } }),
  numberCheck: (input: { country: string; registrationNo?: string; vatNo?: string }) =>
    api<{ taken: boolean; reserved?: boolean; registrationNo: string | null; vatNo: string | null }>('/api/tenant/v1/tenants/number-check', { method: 'POST', body: input }),
  vatCheck: (country: string, vatNo: string) => api<CompanyVatCheck>('/api/tenant/v1/tenants/vat-check', { method: 'POST', body: { country, vatNo } }),
  joinRequest: (input: { country: string; registrationNo?: string; vatNo?: string }) =>
    api<{ sent: boolean }>('/api/tenant/v1/tenants/join-request', { method: 'POST', body: input }),
  companyDetails: (tenantId: string) => api<{ details: CompanyDetailsState }>('/api/tenant/v1/tenants/current/details', { tenantId }),
  saveCompanyDetails: (tenantId: string, details: Partial<CompanyDetails>) =>
    api<{ details: CompanyDetailsState }>('/api/tenant/v1/tenants/current/details', { method: 'PATCH', tenantId, body: details }),
  updateSettings: (tenantId: string, changes: Partial<CompanySettings>) =>
    api<{ settings: CompanySettings }>('/api/tenant/v1/tenants/current/settings', { method: 'PATCH', tenantId, body: changes }).then((body) => body.settings),
  /** "Access follows teams" on or off (TF-300): the company's owners only. */
  setTeamAccess: (tenantId: string, on: boolean) =>
    api<{ settings: CompanySettings }>('/api/tenant/v1/tenants/current/team-access', { method: 'PUT', tenantId, body: { on } }).then((body) => body.settings),
  /** The company's brand colour (TF-109): its owners and admins set it, everybody's colours follow. */
  setAppearance: (tenantId: string, appearance: Appearance | null) =>
    api<{ appearance: Appearance | null }>(`/api/tenant/v1/tenants/${tenantId}/appearance`, { method: 'PUT', tenantId, body: { appearance } }).then((body) => body.appearance),
  members: (tenantId: string) => api<{ members: Member[] }>('/api/tenant/v1/members', { tenantId }).then((body) => body.members),
  memberRoster: (tenantId: string) => api<MemberRoster>('/api/tenant/v1/members', { tenantId }),
  /** Who stays active when the plan allows fewer people: owners always, and these (TF-116). */
  chooseActive: (tenantId: string, userIds: string[]) => api<MemberRoster>('/api/tenant/v1/members/active', { method: 'PUT', tenantId, body: { userIds } }),
  setRole: (tenantId: string, userId: string, role: Role) =>
    api<Member>(`/api/tenant/v1/members/${userId}`, { method: 'PATCH', tenantId, body: { role } }),
  /** Office editing for one person, on a plan that sells it per person (TF-170). */
  setEditing: (tenantId: string, userId: string, canEdit: boolean) =>
    api<{ userId: string; canEdit: boolean }>(`/api/tenant/v1/members/${userId}/editing`, { method: 'PUT', tenantId, body: { canEdit } }),
  removeMember: (tenantId: string, userId: string) => api<void>(`/api/tenant/v1/members/${userId}`, { method: 'DELETE', tenantId }),
  invitations: (tenantId: string) => api<{ invitations: Invitation[] }>('/api/tenant/v1/invitations', { tenantId }).then((body) => body.invitations),
  invite: (tenantId: string, email: string, role: Role) =>
    api<Invitation & { acceptToken: string }>('/api/tenant/v1/invitations', { method: 'POST', tenantId, body: { email, role } }),
  cancelInvitation: (tenantId: string, id: string) => api<void>(`/api/tenant/v1/invitations/${id}`, { method: 'DELETE', tenantId }),
  myInvitations: () => api<MyInvitations>('/api/tenant/v1/invitations/mine'),
  declineInvitation: (id: string) => api<void>(`/api/tenant/v1/invitations/${id}/decline`, { method: 'POST' }),
  sendVerificationEmail: () => api<{ sentTo?: string; verified?: boolean }>('/api/tenant/v1/invitations/mine/verification-email', { method: 'POST' }),
  acceptInvitation: (id: string) =>
    api<{ tenant: Company; role: Role }>(`/api/tenant/v1/invitations/${id}/accept`, { method: 'POST' }),
  // Teams (TF-296).
  teams: (tenantId: string, params: { q?: string; person?: string; after?: string | null; limit?: number } = {}) => api<TeamList>(`/api/tenant/v1/teams${q(params)}`, { tenantId }),
  team: (tenantId: string, teamId: string) => api<TeamDetails>(`/api/tenant/v1/teams/${teamId}`, { tenantId }),
  createTeam: (tenantId: string, team: { name: string; description: string | null; joining: TeamJoining }) =>
    api<Team>('/api/tenant/v1/teams', { method: 'POST', tenantId, body: team }),
  changeTeam: (tenantId: string, teamId: string, change: { name?: string; description?: string | null; joining?: TeamJoining }) =>
    api<Team>(`/api/tenant/v1/teams/${teamId}`, { method: 'PATCH', tenantId, body: change }),
  deleteTeam: (tenantId: string, teamId: string) => api<void>(`/api/tenant/v1/teams/${teamId}`, { method: 'DELETE', tenantId }),
  // Hidden from admins (TF-355): owners only; first how many records the team holds, for the warning.
  teamRecordCount: (tenantId: string, teamId: string) => api<{ records: number }>(`/api/tenant/v1/teams/${teamId}/records`, { tenantId }),
  setTeamHidden: (tenantId: string, teamId: string, on: boolean) => api<Team>(`/api/tenant/v1/teams/${teamId}/hidden`, { method: 'PUT', tenantId, body: { on } }),
  // What the team's people hear about unless they choose otherwise, and its quiet days (TF-301).
  teamNotices: (tenantId: string, teamId: string) => api<TeamNoticeDefaults>(`/api/tenant/v1/teams/${teamId}/notices`, { tenantId }),
  teamOutside: (tenantId: string, teamId: string) => api<TeamOutside>(`/api/tenant/v1/teams/${teamId}/outside`, { tenantId }),
  setTeamOutside: (tenantId: string, teamId: string, change: TeamOutsideChange) => api<TeamOutside>(`/api/tenant/v1/teams/${teamId}/outside`, { method: 'PUT', tenantId, body: change }),
  addOutsidePerson: (tenantId: string, teamId: string, email: string) =>
    api<{ status: 'added' | 'invited' }>(`/api/tenant/v1/teams/${teamId}/outside/people`, { method: 'POST', tenantId, body: { email } }),
  setTeamNotices: (tenantId: string, teamId: string, notices: { defaults: Record<string, boolean>; quietDays: number }) =>
    api<TeamNoticeDefaults>(`/api/tenant/v1/teams/${teamId}/notices`, { method: 'PUT', tenantId, body: notices }),
  /** Every record of the team gets `to` as well, so the team can be deleted (TF-298). */
  moveTeamRecords: (tenantId: string, teamId: string, to: string) =>
    api<{ moved: number }>(`/api/tenant/v1/teams/${teamId}/move-records`, { method: 'POST', tenantId, body: { to } }),
  teamMembers: (tenantId: string, teamId: string, after?: string | null) =>
    api<{ members: TeamMember[]; next: string | null }>(`/api/tenant/v1/teams/${teamId}/members${q({ after })}`, { tenantId }),
  /** Adds somebody (yourself: joining) or changes their role. */
  setTeamMember: (tenantId: string, teamId: string, userId: string, role: TeamRole) =>
    api<{ userId: string; role: TeamRole }>(`/api/tenant/v1/teams/${teamId}/members/${userId}`, { method: 'PUT', tenantId, body: { role } }),
  /** The team a person's new records get (TF-299); null: "All company". Owners and admins. */
  setDefaultTeam: (tenantId: string, userId: string, teamId: string | null) =>
    api<{ userId: string; defaultTeamId: string | null; defaultTeamName: string | null }>(`/api/tenant/v1/members/${userId}/default-team`, { method: 'PUT', tenantId, body: { teamId } }),
  /** Removes somebody, or yourself: leaving. */
  removeTeamMember: (tenantId: string, teamId: string, userId: string) => api<void>(`/api/tenant/v1/teams/${teamId}/members/${userId}`, { method: 'DELETE', tenantId }),
};

export const filesApi = {
  list: (tenantId: string, folderId: string | null, cursor?: string | null) =>
    api<Listing>(`/api/files/v1/items${q({ folderId: folderId ?? 'root', cursor })}`, { tenantId }),
  file: (tenantId: string, fileId: string) => api<FileDetails>(`/api/files/v1/files/${fileId}`, { tenantId }),
  /** One folder's sub-folders, a page at a time, for the folder tree (TF-361). */
  folders: (tenantId: string, parentId: string | null, cursor?: string | null, mayAdd = false) =>
    api<{ folders: TreeFolder[]; nextCursor: string | null }>(`/api/files/v1/folders${q({ parentId: parentId ?? 'root', cursor, mayAdd: mayAdd ? '1' : undefined })}`, { tenantId }),
  /** What moving these to a folder would do (TF-431): what may go, the names taken there, how access changes. */
  checkMove: (tenantId: string, items: MoveItemRef[], folderId: string | null) =>
    api<MoveCheck>('/api/files/v1/moves/check', { method: 'POST', tenantId, body: { items, folderId } }),
  move: (tenantId: string, input: { items: MoveItemRef[]; folderId: string | null; choices?: Record<string, MoveChoice>; acceptAccessChange?: boolean }) =>
    api<MoveResult>('/api/files/v1/moves', { method: 'POST', tenantId, body: input }),
  createFolder: (tenantId: string, name: string, parentId: string | null) =>
    api<Folder>('/api/files/v1/folders', { method: 'POST', tenantId, body: { name, parentId } }),
  updateFolder: (tenantId: string, id: string, patch: { name?: string; parentId?: string | null; restricted?: boolean }) =>
    api<Folder>(`/api/files/v1/folders/${id}`, { method: 'PATCH', tenantId, body: patch }),
  trashFolder: (tenantId: string, id: string) => api<void>(`/api/files/v1/folders/${id}`, { method: 'DELETE', tenantId }),
  restoreFolder: (tenantId: string, id: string) => api<Folder>(`/api/files/v1/folders/${id}/restore`, { method: 'POST', tenantId }),
  announceFile: (tenantId: string, input: { name: string; folderId: string | null; sizeBytes: number; contentType?: string }) =>
    api<{ file: FileItem; upload: UploadIntent }>('/api/files/v1/files', { method: 'POST', tenantId, body: input }),
  announceVersion: (tenantId: string, fileId: string, input: { sizeBytes: number; contentType?: string }) =>
    api<{ upload: UploadIntent }>(`/api/files/v1/files/${fileId}/versions`, { method: 'POST', tenantId, body: input }),
  updateFile: (tenantId: string, id: string, patch: { name?: string; folderId?: string | null }) =>
    api<FileItem>(`/api/files/v1/files/${id}`, { method: 'PATCH', tenantId, body: patch }),
  trashFile: (tenantId: string, id: string) => api<void>(`/api/files/v1/files/${id}`, { method: 'DELETE', tenantId }),
  restoreFile: (tenantId: string, id: string) => api<FileItem>(`/api/files/v1/files/${id}/restore`, { method: 'POST', tenantId }),
  trash: (tenantId: string) => api<{ folders: Folder[]; files: FileItem[] }>('/api/files/v1/trash', { tenantId }),
  purge: (tenantId: string, kind: 'folders' | 'files', id: string) => api<void>(`/api/files/v1/trash/${kind}/${id}`, { method: 'DELETE', tenantId }),
  emptyTrash: (tenantId: string) => api<{ purged: number }>('/api/files/v1/trash', { method: 'DELETE', tenantId }),
  /** What changed lately across the company, already filtered by what the caller may see. */
  /** My pins and the company's (TF-16). */
  pins: (tenantId: string) => api<Pins>('/api/files/v1/pins', { tenantId }),
  pin: (tenantId: string, type: PinType, id: string, company = false) =>
    api<void>(`/api/files/v1/pins/${type}/${id}${company ? '?company=true' : ''}`, { method: 'PUT', tenantId }),
  unpin: (tenantId: string, type: PinType, id: string, company = false) =>
    api<void>(`/api/files/v1/pins/${type}/${id}${company ? '?company=true' : ''}`, { method: 'DELETE', tenantId }),
  /** Across all folders, narrowed (TF-81): the files the person may open, newest change first. */
  // Teams on a folder or file (TF-297): its own and those it has from folders above it.
  itemTeams: (tenantId: string, kind: 'folders' | 'files', id: string) => api<ItemTeams>(`/api/files/v1/${kind}/${id}/teams`, { tenantId }),
  addItemTeam: (tenantId: string, kind: 'folders' | 'files', id: string, teamId: string) =>
    api<{ id: string }>(`/api/files/v1/${kind}/${id}/teams`, { method: 'POST', tenantId, body: { teamId } }),
  removeItemTeam: (tenantId: string, kind: 'folders' | 'files', id: string, teamId: string) =>
    api<void>(`/api/files/v1/${kind}/${id}/teams/${teamId}`, { method: 'DELETE', tenantId }),
  // Tags on folders and files (TF-356): the company's tags (crmApi.tags), kept by id here.
  itemTags: (tenantId: string, kind: 'folders' | 'files', id: string) => api<ItemTags>(`/api/files/v1/${kind}/${id}/tags`, { tenantId }),
  addItemTag: (tenantId: string, kind: 'folders' | 'files', id: string, tagId: string) =>
    api<TagRef>(`/api/files/v1/${kind}/${id}/tags`, { method: 'POST', tenantId, body: { tagId } }),
  removeItemTag: (tenantId: string, kind: 'folders' | 'files', id: string, tagId: string) =>
    api<void>(`/api/files/v1/${kind}/${id}/tags/${tagId}`, { method: 'DELETE', tenantId }),
  tagItems: (tenantId: string, tagId: string, items: { type: 'folder' | 'file'; id: string }[], action: 'add' | 'remove') =>
    api<{ changed: number }>(`/api/files/v1/tags/${tagId}/items`, { method: 'POST', tenantId, body: { items, action } }),
  /** How many folders and files have each tag; null when this person may not count them all. */
  tagDocumentCounts: (tenantId: string) => api<{ counts: Record<string, number> | null }>('/api/files/v1/tags/counts', { tenantId }),
  find: (tenantId: string, filter: FileFind, cursor?: string | null, limit?: number) =>
    api<FileFindPage>('/api/files/v1/files/find', { method: 'POST', tenantId, body: { ...filter, ...(cursor ? { cursor } : {}), ...(limit ? { limit } : {}) } }),
  /** The files I opened, uploaded or attached last (TF-114), that I may still open. */
  myRecent: (tenantId: string, limit = 20) =>
    api<{ files: RecentFile[] }>(`/api/files/v1/recent/mine${q({ limit })}`, { tenantId }).then((body) => body.files),
  recent: (tenantId: string, limit = 8) =>
    api<{ files: FileItem[] }>(`/api/files/v1/recent${q({ limit })}`, { tenantId }).then((body) => body.files),
  usage: (tenantId: string) => api<Usage>('/api/files/v1/usage', { tenantId }),
};

export const downloadApi = {
  file: (tenantId: string, fileId: string, options: { inline?: boolean; versionId?: string } = {}) =>
    api<SignedUrl>(`/api/download/v1/files/${fileId}${q({ disposition: options.inline ? 'inline' : undefined, versionId: options.versionId })}`, { tenantId }),
  preview: (tenantId: string, fileId: string, kind: 'thumbnail' | 'document') =>
    api<SignedUrl>(`/api/download/v1/files/${fileId}/preview${q({ kind })}`, { tenantId }),
};

export const sharingApi = {
  sharedWithMe: () => api<{ items: SharedItem[] }>('/api/sharing/v1/shared-with-me').then((body) => body.items),
  sharedWithCompany: (tenantId: string) => api<{ items: SharedItem[] }>('/api/sharing/v1/shared-with-company', { tenantId }).then((body) => body.items),
  claim: () => api<{ claimed: number }>('/api/sharing/v1/claims', { method: 'POST' }),
  shares: (tenantId: string, objectType: 'folder' | 'file', objectId: string) =>
    api<{ grants: Grant[]; links: Link[]; sensitiveTags?: { id: string; name: string }[] }>(`/api/sharing/v1/grants${q({ objectType, objectId })}`, { tenantId }),
  shareWithPerson: (tenantId: string, input: { objectType: 'folder' | 'file'; objectId: string; role: 'viewer' | 'editor'; email: string }) =>
    api<Grant>('/api/sharing/v1/grants', { method: 'POST', tenantId, body: input }),
  shareWithCompany: (tenantId: string, input: { objectType: 'folder' | 'file'; objectId: string; role: 'viewer' | 'editor'; tenantSlug: string }) =>
    api<Grant>('/api/sharing/v1/grants', { method: 'POST', tenantId, body: input }),
  revokeGrant: (tenantId: string, id: string) => api<void>(`/api/sharing/v1/grants/${id}`, { method: 'DELETE', tenantId }),
  createLink: (
    tenantId: string,
    input: { objectType: 'folder' | 'file'; objectId: string; password?: string; expiresAt?: string; maxDownloads?: number },
  ) => api<CreatedLink>('/api/sharing/v1/links', { method: 'POST', tenantId, body: input }),
  revokeLink: (tenantId: string, id: string) => api<void>(`/api/sharing/v1/links/${id}`, { method: 'DELETE', tenantId }),
  openLink: (token: string, password: string | null, folderId?: string | null) =>
    api<PublicLink>(`/api/sharing/v1/public/links/${token}${q({ folderId })}`, {
      anonymous: true,
      headers: password ? { 'x-link-password': password } : {},
    }),
  downloadViaLink: (token: string, password: string | null, fileId?: string) =>
    api<SignedUrl>(`/api/sharing/v1/public/links/${token}/download`, {
      method: 'POST',
      anonymous: true,
      headers: password ? { 'x-link-password': password } : {},
      body: fileId ? { fileId } : {},
    }),
};

export const searchApi = {
  search: (tenantId: string, text: string, kinds?: string) => api<{ hits: SearchHit[] }>(`/api/search/v1${q({ q: text, kinds, limit: 50 })}`, { tenantId }).then((body) => body.hits),
};

export const notificationsApi = {
  list: () => api<{ items: AppNotification[] }>('/api/notifications/v1/notifications?limit=30').then((body) => body.items),
  unread: () => api<{ unread: number }>('/api/notifications/v1/unread-count').then((body) => body.unread),
  markRead: (id: string) => api<void>(`/api/notifications/v1/notifications/${id}/read`, { method: 'POST' }),
  markAllRead: () => api<{ marked: number; ids: string[] }>('/api/notifications/v1/read-all', { method: 'POST' }),
  markUnread: (ids: string[]) => api<{ unread: number }>('/api/notifications/v1/unread', { method: 'POST', body: { ids } }),
  preferences: () => api<{ preferences: Preference[] }>('/api/notifications/v1/preferences').then((body) => body.preferences),
  savePreferences: (preferences: { type: string; email: boolean; inApp: boolean; push?: boolean }[]) =>
    api<void>('/api/notifications/v1/preferences', { method: 'PUT', body: { preferences } }),
  // My team notice choices in a company (TF-301); null puts a kind back to the team's default.
  teamNotices: (tenantId: string) => api<{ choices: TeamNoticeChoice[] }>('/api/notifications/v1/team-notices', { tenantId }).then((body) => body.choices),
  // Mail logging (TF-151): my address in a company, and a new one in its place.
  mailLogAddress: (tenantId: string) => api<MailLogAddress>('/api/notifications/v1/mail-log-address', { tenantId }),
  renewMailLogAddress: (tenantId: string) => api<MailLogAddress>('/api/notifications/v1/mail-log-address/renew', { method: 'POST', tenantId }),
  setTeamNotices: (tenantId: string, scope: string, choices: Record<string, boolean | null>) =>
    api<void>('/api/notifications/v1/team-notices', { method: 'PUT', tenantId, body: { scope, choices } }),
  // The company's own sending domain (TF-211), for its owners and admins.
  sendingDomain: (tenantId: string) => api<SendingDomainView>('/api/notifications/v1/sending-domain', { tenantId }),
  setUpSendingDomain: (tenantId: string, input: { domain: string; fromLocal: string }) =>
    api<SendingDomainView>('/api/notifications/v1/sending-domain', { method: 'PUT', tenantId, body: input }),
  changeSendingAddress: (tenantId: string, fromLocal: string) =>
    api<SendingDomainView>('/api/notifications/v1/sending-domain', { method: 'PATCH', tenantId, body: { fromLocal } }),
  checkSendingDomain: (tenantId: string) => api<SendingDomainView>('/api/notifications/v1/sending-domain/check', { method: 'POST', tenantId }),
  removeSendingDomain: (tenantId: string) => api<void>('/api/notifications/v1/sending-domain', { method: 'DELETE', tenantId }),
};

export const privacyApi = {
  myData: () => api<Record<string, unknown>>('/api/privacy/v1/me/export'),
  deleteAccount: (confirm: string) => api<{ id: string }>('/api/privacy/v1/me/erasure', { method: 'POST', body: { confirm } }),
};

// ------------------------------------------------------------------------------------ CRM

const CRM = '/api/crm/v1';
// Tasks are activities to the API, for their teams (TF-297) and tags (TF-354).
const TEAM_PATHS: Record<TeamRecordType | TagRecordType, string> = { account: 'accounts', contact: 'contacts', deal: 'deals', task: 'activities' };
const RECORD_PATHS: Record<AttachmentTarget, string> = { account: 'accounts', contact: 'contacts', deal: 'deals', task: 'tasks' };

/** Fields a form sends. null clears a field; a custom value of null removes it. */
export type AccountInput = Partial<Omit<Account, 'id' | 'createdAt' | 'updatedAt' | 'tags' | 'teamIds'>>;
/** ?tags=<id,id>&tagMode=any|all on the accounts and contacts lists (TF-27), and the deals board and list (TF-48). */
export interface TagQuery {
  tags?: string;
  tagMode?: 'any' | 'all';
  /** ?teams=<id,id>&teamMode=any|all (TF-297): the app resolves "My teams" to ids. */
  teams?: string;
  teamMode?: 'any' | 'all';
}
export type ContactInput = Partial<Omit<Contact, 'id' | 'accountName' | 'createdAt' | 'updatedAt' | 'tags' | 'teamIds'>>;
export interface DealInput {
  /** Seen only by its owner, the colleagues it is shared with and admins (TF-47). */
  private?: boolean;
  title?: string;
  accountId?: string | null;
  contactId?: string | null;
  stageId?: string;
  amount?: number | null;
  currency?: string;
  expectedCloseDate?: string | null;
  ownerId?: string | null;
  /** The deal's own probability, 0 to 100; null is its stage's (TF-172). */
  probability?: number | null;
  customFields?: CustomValues;
}
export interface ActivityInput {
  type: UserActivityType;
  /** Tasks only; left out, a task is a to-do. */
  category?: TaskCategory | null;
  subject: string;
  body?: string | null;
  dueAt?: string | null;
  /** A task at any time that day (TF-220), instead of dueAt. */
  dueDay?: string | null;
  /** The zone the due time was entered in (TF-220). */
  dueZone?: string | null;
  accountId?: string | null;
  contactId?: string | null;
  dealId?: string | null;
  assignedTo?: string | null;
  /** E-mails (TF-49), sent when left out; calls (TF-52), outgoing when left out. */
  direction?: EmailDirection | CallDirection | null;
  /** Calls only (TF-45). */
  outcome?: CallOutcome | null;
  /** Calls only (TF-52): when it happened, if not now. */
  doneAt?: string | null;
  /** A planned meeting only (TF-360): when it ends (left out, an hour after it starts), where, and who is asked. */
  endsAt?: string | null;
  location?: string | null;
  attendees?: AttendeeInput[];
  /** E-mail the invitation to the people outside the company (TF-363). */
  sendInvitations?: boolean;
}
export interface ActivityPatch {
  category?: TaskCategory;
  status?: TaskStatus;
  subject?: string;
  body?: string | null;
  dueAt?: string | null;
  /** TF-220: a day instead of a moment; the zone a new time is in (left out, the task keeps its zone). */
  dueDay?: string | null;
  dueZone?: string | null;
  assignedTo?: string | null;
  done?: boolean;
  /** A task only: where it belongs now. Send all three; null means "not there" (TF-11). */
  accountId?: string | null;
  contactId?: string | null;
  dealId?: string | null;
  /** A planned meeting only (TF-360). Attendees, when sent, are all of them; left out, they stay. */
  endsAt?: string | null;
  location?: string | null;
  attendees?: AttendeeInput[];
  /**
   * TF-363: true sends the invitations now; false keeps a change from being sent to the people already
   * invited; left out, a meeting already invited sends its changes on its own.
   */
  sendInvitations?: boolean;
}
/** ?cf.<key>=<option>|<option> or =true|false: a list filtered by a choice or yes/no custom field (TF-28). */
/** "More filters" in the address: ?cf.<key>= custom fields (TF-28), ?pref.<category>= communication preferences (TF-30). */
export type CustomFieldQuery = Record<`cf.${string}` | `pref.${string}`, string>;

/** The contacts list's filters and order (TF-27, TF-28, TF-38, TF-55, TF-56). */
/** The Accounts list's filters (TF-24, TF-27, TF-28, TF-39, TF-59, TF-108). */
export interface AccountQuery extends Partial<CustomFieldQuery>, TagQuery {
  q?: string;
  kind?: string;
  /** What one import made (TF-164). */
  importId?: string;
  lastContacted?: LastContacted;
  /** Comma-separated AccountMissing keys. */
  missing?: string;
  missingMode?: 'any' | 'all';
  sort?: 'lastContacted';
  /** The first page also says how many match (Page.total). */
  count?: 'true';
}

export interface ContactQuery extends Partial<CustomFieldQuery>, TagQuery {
  q?: string;
  accountId?: string;
  /** What one import made (TF-164). */
  importId?: string;
  mine?: 'true';
  lastContacted?: LastContacted;
  /** Comma-separated ContactMissing keys. */
  missing?: string;
  missingMode?: 'any' | 'all';
  sort?: 'lastContacted';
  /** The first page also says how many match (Page.total). */
  count?: 'true';
}

export interface DealFilter extends Partial<CustomFieldQuery>, TagQuery {
  q?: string;
  /** Expected to close in [closeFrom, closeTo), dates YYYY-MM-DD (TF-12). */
  closeFrom?: string;
  closeTo?: string;
  accountId?: string;
  contactId?: string;
  stageId?: string;
  status?: string;
  /** What it is worth: an operator and a number, so "at least 5000" is one control, not two. */
  amountOp?: 'gte' | 'lte' | 'eq';
  amount?: string;
  /** Or a range, from (included) to (not included): a column of Deals by size (TF-222). */
  amountFrom?: string;
  amountTo?: string;
  /** "true": the deals with no amount (TF-222). */
  amountNone?: string;
  /** One currency's deals (TF-222). */
  currency?: string;
  /** Sitting in its stage for at least this many days. */
  inStageDays?: string;
  /** A member's id, or "me". */
  ownerId?: string;
  /** One pipeline's deals, in the list (TF-135). */
  pipelineId?: string;
  /** Closed in [closedFrom, closedTo), instants: a chart's Won or Lost for its period (TF-135). */
  closedFrom?: string;
  closedTo?: string;
}
export interface ActivityFilter {
  /** Words in the subject (TF-25). */
  q?: string;
  /** Tasks of these teams (TF-297). */
  teams?: string;
  teamMode?: 'any' | 'all';
  /** Tasks with these tags (TF-354). */
  tags?: string;
  tagMode?: 'any' | 'all';
  /** One or more task categories, comma-separated (TF-10). */
  category?: string;
  /** One or more task statuses, comma-separated (TF-9). */
  status?: string;
  /** "due": soonest-due first, undated last; "open-first": open tasks first, then done, each soonest due (TF-49); left out, newest first. */
  sort?: 'due' | 'open-first';
  /** Due in [dueFrom, dueTo), instants (TF-12). */
  dueFrom?: string;
  dueTo?: string;
  accountId?: string;
  contactId?: string;
  dealId?: string;
  /** One or more types, comma-separated (TF-49). */
  type?: string;
  /** Touches with the customer, comma-separated: any, sent, received, call, meeting (TF-49). */
  touch?: string;
  open?: boolean;
  assignedTo?: string;
  /** "true": the first page says how many match (the calendar's list, TF-115). */
  count?: 'true';
}

export const crmApi = {
  /** Before access follows teams (TF-300): how many records each of these people would no longer see. */
  teamAccessPreview: (tenantId: string, userIds: string[]) =>
    api<{ people: { userId: string; records: number }[] }>(`${CRM}/team-access/preview`, { method: 'POST', tenantId, body: { userIds } }).then((body) => body.people),
  /** Where the company is (TF-61): the time zone its "today" is in. */
  locale: (tenantId: string) => api<{ timeZone: string; language: BackendLanguage }>(`${CRM}/locale`, { tenantId }),
  accounts: (tenantId: string, filter: AccountQuery = {}, cursor?: string | null, limit?: number) =>
    api<Page<Account>>(`${CRM}/accounts${q({ ...filter, cursor, limit })}`, { tenantId }),
  account: (tenantId: string, id: string) => api<AccountDetails>(`${CRM}/accounts/${id}`, { tenantId }),
  // Tags (TF-27): the company's list, one record's, many records at once, and tidying up.
  tags: (tenantId: string, text?: string) => api<{ items: Tag[] }>(`${CRM}/tags${q({ q: text || undefined })}`, { tenantId }).then((body) => body.items),
  tagRecord: (tenantId: string, record: TagRecordType, id: string, tag: { tagId: string } | { name: string }) =>
    api<TagRef>(`${CRM}/${TEAM_PATHS[record]}/${id}/tags`, { method: 'POST', tenantId, body: tag }),
  untagRecord: (tenantId: string, record: TagRecordType, id: string, tagId: string) =>
    api<void>(`${CRM}/${TEAM_PATHS[record]}/${id}/tags/${tagId}`, { method: 'DELETE', tenantId }),
  // Teams (TF-297): one record's, and many records at once. Tasks are activities to the API.
  addRecordTeam: (tenantId: string, record: TeamRecordType, id: string, teamId: string) =>
    api<{ id: string }>(`${CRM}/${TEAM_PATHS[record]}/${id}/teams`, { method: 'POST', tenantId, body: { teamId } }),
  removeRecordTeam: (tenantId: string, record: TeamRecordType, id: string, teamId: string) =>
    api<void>(`${CRM}/${TEAM_PATHS[record]}/${id}/teams/${teamId}`, { method: 'DELETE', tenantId }),
  teamRecords: (tenantId: string, teamId: string, recordType: TeamRecordType, recordIds: string[], action: 'add' | 'remove') =>
    api<{ changed: number; kept?: number }>(`${CRM}/teams/${teamId}/records`, { method: 'POST', tenantId, body: { recordType, recordIds, action } }),
  createTag: (tenantId: string, name: string) => api<TagRef>(`${CRM}/tags`, { method: 'POST', tenantId, body: { name } }),
  tagRecords: (tenantId: string, tagId: string, recordType: TagRecordType, recordIds: string[], action: 'add' | 'remove') =>
    api<{ changed: number }>(`${CRM}/tags/${tagId}/records`, { method: 'POST', tenantId, body: { recordType, recordIds, action } }),
  renameTag: (tenantId: string, tagId: string, name: string) => api<TagRef>(`${CRM}/tags/${tagId}`, { method: 'PATCH', tenantId, body: { name } }),
  mergeTag: (tenantId: string, tagId: string, into: string) => api<TagRef>(`${CRM}/tags/${tagId}/merge`, { method: 'POST', tenantId, body: { into } }),
  deleteTag: (tenantId: string, tagId: string) => api<void>(`${CRM}/tags/${tagId}`, { method: 'DELETE', tenantId }),
  // Sensitive tags (TF-357): the table, the switch, and the table saved whole.
  tagTable: (tenantId: string, tagId: string) => api<TagTable>(`${CRM}/tags/${tagId}/grants`, { tenantId }),
  setTagSensitive: (tenantId: string, tagId: string, on: boolean) =>
    api<TagTable>(`${CRM}/tags/${tagId}/sensitive`, { method: 'PUT', tenantId, body: { on } }),
  setTagGrants: (tenantId: string, tagId: string, grants: TagGrant[]) =>
    api<TagTable>(`${CRM}/tags/${tagId}/grants`, { method: 'PUT', tenantId, body: { grants } }),
  // A contact at several accounts (TF-31).
  contactAccounts: (tenantId: string, contactId: string) =>
    api<{ items: ContactAccountLink[] }>(`${CRM}/contacts/${contactId}/accounts`, { tenantId }).then((body) => body.items),
  linkContactAccount: (tenantId: string, contactId: string, input: { accountId: string; roleId?: string; primary?: boolean }) =>
    api<{ items: ContactAccountLink[] }>(`${CRM}/contacts/${contactId}/accounts`, { method: 'POST', tenantId, body: input }).then((body) => body.items),
  updateContactAccount: (tenantId: string, contactId: string, accountId: string, patch: { roleId?: string; primary?: true }) =>
    api<{ items: ContactAccountLink[] }>(`${CRM}/contacts/${contactId}/accounts/${accountId}`, { method: 'PATCH', tenantId, body: patch }).then((body) => body.items),
  unlinkContactAccount: (tenantId: string, contactId: string, accountId: string, primary?: string) =>
    api<{ items: ContactAccountLink[] }>(`${CRM}/contacts/${contactId}/accounts/${accountId}${q({ primary })}`, { method: 'DELETE', tenantId }).then((body) => body.items),
  accountPeople: (tenantId: string, accountId: string) =>
    api<{ items: ContactAccountLink[] }>(`${CRM}/accounts/${accountId}/people`, { tenantId }).then((body) => body.items),
  // Communication preferences (TF-30).
  communicationCategories: (tenantId: string) =>
    api<{ categories: CommunicationCategory[] }>(`${CRM}/communication-categories`, { tenantId }).then((body) => body.categories),
  createCommunicationCategory: (tenantId: string, name: string) =>
    api<CommunicationCategory>(`${CRM}/communication-categories`, { method: 'POST', tenantId, body: { name } }),
  updateCommunicationCategory: (tenantId: string, id: string, patch: Partial<Pick<CommunicationCategory, 'name' | 'position' | 'active'>>) =>
    api<CommunicationCategory>(`${CRM}/communication-categories/${id}`, { method: 'PATCH', tenantId, body: patch }),
  deleteCommunicationCategory: (tenantId: string, id: string) => api<void>(`${CRM}/communication-categories/${id}`, { method: 'DELETE', tenantId }),
  /** A contact's, with their company's and what applies (TF-39); an account's, its own. */
  contactPreferences: (tenantId: string, contactId: string) => api<ContactPreferencesView>(`${CRM}/contacts/${contactId}/preferences`, { tenantId }),
  accountPreferences: (tenantId: string, accountId: string) =>
    api<{ preferences: CurrentPreference[] }>(`${CRM}/accounts/${accountId}/preferences`, { tenantId }).then((body) => body.preferences),
  preferenceHistory: (tenantId: string, record: 'contact' | 'account', id: string) =>
    api<{ items: PreferenceChangeEntry[] }>(`${CRM}/${record}s/${id}/preferences/history`, { tenantId }).then((body) => body.items),
  setPreferences: (
    tenantId: string,
    record: 'contact' | 'account',
    id: string,
    input: { changes: { categoryId: string | null; state: PreferenceState | 'not_asked' }[]; method: ConsentMethod; note: string | null },
  ) => api<{ changed: number; preferences: CurrentPreference[] }>(`${CRM}/${record}s/${id}/preferences`, { method: 'PUT', tenantId, body: input }),
  // Deal lines settings (TF-321): each list written whole, in its order.
  // Products and services on a deal (TF-251): written whole; the server computes the amounts.
  dealLines: (tenantId: string, dealId: string) => api<{ lines: DealLine[]; totals: DealLineTotals }>(`${CRM}/deals/${dealId}/lines`, { tenantId }),
  saveDealLines: (tenantId: string, dealId: string, lines: DealLineInput[]) =>
    api<{ lines: DealLine[]; totals: DealLineTotals }>(`${CRM}/deals/${dealId}/lines`, { method: 'PUT', tenantId, body: { lines } }),
  dealLineSuggestions: (tenantId: string, q: string) =>
    api<{ items: DealLineSuggestion[] }>(`${CRM}/deal-line-suggestions${q ? `?q=${encodeURIComponent(q)}` : ''}`, { tenantId }).then((body) => body.items),
  dealLineSettings: (tenantId: string) => api<DealLineSettings>(`${CRM}/deal-line-settings`, { tenantId }),
  /** Addresses for a meeting's place (TF-364): the text goes in the body, never in an address. */
  placeSuggest: (tenantId: string, text: string, session: string, language: string) =>
    api<PlaceSearch>(`${CRM}/places/suggest`, { method: 'POST', tenantId, body: { text, session, language } }),
  placeResolve: (tenantId: string, id: string, session: string, language: string) =>
    api<{ text: string }>(`${CRM}/places/resolve`, { method: 'POST', tenantId, body: { id, session, language } }).then((body) => body.text),
  saveDealLineList: (tenantId: string, list: DealLineList, entries: LineOptionInput[]) =>
    api<DealLineSettings>(`${CRM}/deal-line-settings/${list}`, { method: 'PUT', tenantId, body: { entries } }),
  restoreDealLineList: (tenantId: string, list: DealLineList) => api<DealLineSettings>(`${CRM}/deal-line-settings/${list}/restore`, { method: 'POST', tenantId }),
  saveOpenEndedMonths: (tenantId: string, openEndedMonths: number) =>
    api<{ openEndedMonths: number; dealsChanged: number }>(`${CRM}/deal-line-settings/preferences`, { method: 'PUT', tenantId, body: { openEndedMonths } }),
  // Linked accounts (TF-22).
  /** Links between accounts, or a contact's roles at an account (TF-31). */
  relationTypes: (tenantId: string, appliesTo: 'account' | 'contact' = 'account') =>
    api<{ types: RelationType[] }>(`${CRM}/relation-types?appliesTo=${appliesTo}`, { tenantId }).then((body) => body.types),
  createRelationType: (tenantId: string, input: { nameA: string; nameB?: string; hierarchical?: boolean }, appliesTo: 'account' | 'contact' = 'account') =>
    api<RelationType>(`${CRM}/relation-types`, { method: 'POST', tenantId, body: { appliesTo, ...input } }),
  updateRelationType: (tenantId: string, id: string, patch: Partial<Pick<RelationType, 'nameA' | 'nameB' | 'hierarchical' | 'active' | 'position'>>) =>
    api<RelationType>(`${CRM}/relation-types/${id}`, { method: 'PATCH', tenantId, body: patch }),
  deleteRelationType: (tenantId: string, id: string) => api<void>(`${CRM}/relation-types/${id}`, { method: 'DELETE', tenantId }),
  accountRelations: (tenantId: string, accountId: string) =>
    api<{ items: AccountRelation[] }>(`${CRM}/accounts/${accountId}/relations`, { tenantId }).then((body) => body.items),
  relateAccount: (tenantId: string, accountId: string, input: { typeId: string; side: 'a' | 'b'; accountId: string; note: string | null }) =>
    api<AccountRelation>(`${CRM}/accounts/${accountId}/relations`, { method: 'POST', tenantId, body: input }),
  updateRelation: (tenantId: string, accountId: string, relationId: string, note: string | null) =>
    api<AccountRelation>(`${CRM}/accounts/${accountId}/relations/${relationId}`, { method: 'PATCH', tenantId, body: { note } }),
  unrelateAccount: (tenantId: string, accountId: string, relationId: string) =>
    api<void>(`${CRM}/accounts/${accountId}/relations/${relationId}`, { method: 'DELETE', tenantId }),
  createAccount: (tenantId: string, input: AccountInput) => api<Account>(`${CRM}/accounts`, { method: 'POST', tenantId, body: input }),
  updateAccount: (tenantId: string, id: string, patch: AccountInput) => api<Account>(`${CRM}/accounts/${id}`, { method: 'PATCH', tenantId, body: patch }),
  deleteAccount: (tenantId: string, id: string) => api<void>(`${CRM}/accounts/${id}`, { method: 'DELETE', tenantId }),

  // Import of accounts and contacts (TF-164): the browser reads the file and sends its rows in parts.
  imports: (tenantId: string) => api<{ items: ImportRecord[] }>(`${CRM}/imports`, { tenantId }).then((body) => body.items),
  importItem: (tenantId: string, id: string) => api<ImportRecord>(`${CRM}/imports/${id}`, { tenantId }),
  lastImportMapping: (tenantId: string, entity: ImportEntity) =>
    api<{ headings: string[]; mapping: (string | null)[] }>(`${CRM}/imports/last-mapping${q({ entity })}`, { tenantId }),
  createImport: (tenantId: string, input: { entity: ImportEntity; fileName: string; headings: string[]; rowsTotal: number; source?: 'file' | 'doculat'; exportCompanyId?: string | null }) =>
    api<ImportRecord>(`${CRM}/imports`, { method: 'POST', tenantId, body: input }),
  sendImportRows: (tenantId: string, id: string, from: number, rows: string[][]) =>
    api<{ received: number; total: number }>(`${CRM}/imports/${id}/rows`, { method: 'POST', tenantId, body: { from, rows } }),
  checkImport: (tenantId: string, id: string, mapping: (string | null)[], options: ImportOptions) =>
    api<ImportCheck>(`${CRM}/imports/${id}/check`, { method: 'POST', tenantId, body: { mapping, options } }),
  startImport: (tenantId: string, id: string) => api<ImportRecord>(`${CRM}/imports/${id}/start`, { method: 'POST', tenantId }),
  importUndo: (tenantId: string, id: string) => api<{ refusal: string | null; preview: ImportUndoPreview | null }>(`${CRM}/imports/${id}/undo`, { tenantId }),
  undoImport: (tenantId: string, id: string) => api<ImportRecord>(`${CRM}/imports/${id}/undo`, { method: 'POST', tenantId }),
  deleteImport: (tenantId: string, id: string) => api<void>(`${CRM}/imports/${id}`, { method: 'DELETE', tenantId }),
  // From the person's own Google Contacts (TF-237): what is there, then an import of the chosen ones, ready to check.
  googleContacts: (tenantId: string) => api<GoogleContactsStatus>(`${CRM}/imports/google`, { tenantId }),
  createGoogleImport: (tenantId: string, input: { labels: string[] | null; onlyNew: boolean }) =>
    api<ImportRecord & { incremental: boolean }>(`${CRM}/imports/google`, { method: 'POST', tenantId, body: input }),
  // ...and from their Outlook / Microsoft 365 contacts (TF-311), by folder.
  microsoftContacts: (tenantId: string) => api<MicrosoftContactsStatus>(`${CRM}/imports/microsoft`, { tenantId }),
  createMicrosoftImport: (tenantId: string, input: { folders: string[] | null; onlyNew: boolean }) =>
    api<ImportRecord & { incremental: boolean }>(`${CRM}/imports/microsoft`, { method: 'POST', tenantId, body: input }),
  importLeftOut: (tenantId: string, id: string) =>
    api<{ headings: string[]; rows: { row: number; cells: string[]; problem: ImportProblem[] | null }[] }>(`${CRM}/imports/${id}/left-out`, { tenantId }),

  contacts: (tenantId: string, filter: ContactQuery = {}, cursor?: string | null, limit?: number) =>
    api<Page<Contact>>(`${CRM}/contacts${q({ ...filter, cursor, limit })}`, { tenantId }),
  contact: (tenantId: string, id: string) => api<Contact>(`${CRM}/contacts/${id}`, { tenantId }),
  createContact: (tenantId: string, input: ContactInput) => api<Contact>(`${CRM}/contacts`, { method: 'POST', tenantId, body: input }),
  updateContact: (tenantId: string, id: string, patch: ContactInput) => api<Contact>(`${CRM}/contacts/${id}`, { method: 'PATCH', tenantId, body: patch }),
  deleteContact: (tenantId: string, id: string) => api<void>(`${CRM}/contacts/${id}`, { method: 'DELETE', tenantId }),
  // A contact's photo (TF-310): the square the browser cropped goes as it is; the server re-encodes it.
  contactPhoto: (tenantId: string, id: string, version: string) => apiDataUrl(`${CRM}/contacts/${id}/photo?v=${encodeURIComponent(version)}`, { tenantId }),
  setContactPhoto: (tenantId: string, id: string, image: Blob) => api<{ photo: string }>(`${CRM}/contacts/${id}/photo`, { method: 'PUT', tenantId, body: image }),
  removeContactPhoto: (tenantId: string, id: string) => api<void>(`${CRM}/contacts/${id}/photo`, { method: 'DELETE', tenantId }),
  /** GDPR "forget me": the contact and everything recorded against them, for good. Admins only. */
  eraseContact: (tenantId: string, id: string) =>
    api<{ erased: boolean; activities: number; deals: number; attachments: number }>(`${CRM}/contacts/${id}/erase`, { method: 'POST', tenantId }),

  pipelines: (tenantId: string) => api<{ pipelines: Pipeline[] }>(`${CRM}/pipelines`, { tenantId }).then((body) => body.pipelines),
  board: (tenantId: string, pipelineId: string, filter: DealFilter = {}) =>
    api<Board>(`${CRM}/pipelines/${pipelineId}/board${q({ ...filter })}`, { tenantId }),
  /** What a pipeline - or "all" of them - holds and closed in a period, for the deal charts (TF-135, TF-221). */
  /** With `team`, only the deals that carry it (TF-393). */
  pipelineSummary: (tenantId: string, pipelineId: string, period: ChartPeriod, team?: string) =>
    api<PipelineSummary>(`${CRM}/pipelines/${pipelineId}/summary${q({ period, team })}`, { tenantId }),
  /** Deals by size (TF-222): of a pipeline, or of "all". */
  dealSizes: (tenantId: string, pipelineId: string, options: { status: 'open' | 'won'; period: ChartPeriod; currency?: string }) =>
    api<DealSizes>(`${CRM}/pipelines/${pipelineId}/sizes${q({ ...options })}`, { tenantId }),
  deals: (tenantId: string, filter: DealFilter = {}, cursor?: string | null, limit?: number) =>
    api<Page<Deal>>(`${CRM}/deals${q({ ...filter, cursor, limit })}`, { tenantId }),
  deal: (tenantId: string, id: string) => api<Deal>(`${CRM}/deals/${id}`, { tenantId }),
  createDeal: (tenantId: string, input: DealInput) => api<Deal>(`${CRM}/deals`, { method: 'POST', tenantId, body: input }),
  updateDeal: (tenantId: string, id: string, patch: DealInput) => api<Deal>(`${CRM}/deals/${id}`, { method: 'PATCH', tenantId, body: patch }),
  deleteDeal: (tenantId: string, id: string) => api<void>(`${CRM}/deals/${id}`, { method: 'DELETE', tenantId }),

  // A range of days is the viewer's (TF-220): a day's task is in it by its date where they are.
  activities: (tenantId: string, filter: ActivityFilter, cursor?: string | null, limit?: number) =>
    api<Page<Activity>>(
      `${CRM}/activities${q({ ...filter, open: filter.open ? 'true' : undefined, zone: filter.dueFrom || filter.dueTo ? viewerZone() : undefined, cursor, limit })}`,
      { tenantId },
    ),
  /** A record's timeline tabs: the count of each tab and chip, and on an account its contacts and deals with entries (TF-49). */
  activityCounts: (tenantId: string, filter: Pick<ActivityFilter, 'accountId' | 'contactId' | 'dealId'>) =>
    api<TimelineCounts>(`${CRM}/activities/counts${q({ ...filter })}`, { tenantId }),
  /** One entry with the names of what it is filed under (the task quick view, TF-17). */
  activity: (tenantId: string, id: string) => api<Activity>(`${CRM}/activities/${id}`, { tenantId }),
  /** How many of my open tasks are past due (TF-93). */
  overdueCount: (tenantId: string) => api<{ overdue: number }>(`${CRM}/activities/overdue${q({ zone: viewerZone() })}`, { tenantId }).then((body) => body.overdue),
  createActivity: (tenantId: string, input: ActivityInput) => api<Activity>(`${CRM}/activities`, { method: 'POST', tenantId, body: input }),
  updateActivity: (tenantId: string, id: string, patch: ActivityPatch) =>
    api<Activity>(`${CRM}/activities/${id}`, { method: 'PATCH', tenantId, body: patch }),
  deleteActivity: (tenantId: string, id: string) => api<void>(`${CRM}/activities/${id}`, { method: 'DELETE', tenantId }),

  fields: (tenantId: string, entity: RecordType) =>
    api<{ fields: FieldDefinition[] }>(`${CRM}/fields/${entity}`, { tenantId }).then((body) => body.fields),
  /** Replaces the whole set; the order given is the order forms show. Admins only. */
  /** renames: saved choices given a new name, so the records that have them follow (TF-28). */
  // removeFromForms: a field no longer allowed on a public form comes off the forms that ask it (TF-328);
  // without it, such a save is refused with 409 FIELD_ON_FORMS, naming the forms.
  saveFields: (tenantId: string, entity: RecordType, fields: FieldInput[], renames: { key: string; from: string; to: string }[] = [], removeFromForms = false) =>
    api<{ fields: FieldDefinition[] }>(`${CRM}/fields/${entity}`, { method: 'PUT', tenantId, body: { fields, renames, ...(removeFromForms ? { removeFromForms } : {}) } }).then(
      (body) => body.fields,
    ),

  attachments: (tenantId: string, record: AttachmentTarget, id: string) =>
    api<{ attachments: Attachment[]; hidden: number; fromTasks: TaskAttachment[] }>(`${CRM}/${RECORD_PATHS[record]}/${id}/attachments`, { tenantId }),
  attach: (tenantId: string, record: AttachmentTarget, id: string, fileId: string) =>
    api<{ fileId: string; fileName: string }>(`${CRM}/${RECORD_PATHS[record]}/${id}/attachments`, { method: 'POST', tenantId, body: { fileId } }),
  detach: (tenantId: string, record: AttachmentTarget, id: string, fileId: string) =>
    api<void>(`${CRM}/${RECORD_PATHS[record]}/${id}/attachments/${fileId}`, { method: 'DELETE', tenantId }),
  /** A VAT number checked in VIES (TF-33), and the name and address it is registered to. */
  companyLookup: (tenantId: string, number: string, kind: 'vat' | 'registration' = 'vat') =>
    api<LookupResult>(`${CRM}/company-lookup`, { method: 'POST', tenantId, body: kind === 'vat' ? { number } : { number, kind } }),
  /** An account's VAT number checked again; the result is kept with it. */
  checkAccountVat: (tenantId: string, id: string) => api<LookupResult>(`${CRM}/accounts/${id}/vat-check`, { method: 'POST', tenantId }),
  /** An account's registration number looked up in its country's register (TF-42); the status is kept with it. */
  checkAccountRegister: (tenantId: string, id: string) => api<LookupResult>(`${CRM}/accounts/${id}/register-check`, { method: 'POST', tenantId }),
  /** Who a contact is shared with (TF-38), and changing it (the whole list). */
  recordShares: (tenantId: string, type: ShareableType, id: string) =>
    api<{ shares: RecordShare[] }>(`${CRM}/${type}s/${id}/shares`, { tenantId }).then((body) => body.shares),
  // How far a colleague reaches the record through its sensitive tags (TF-358): null when it has none.
  shareReach: (tenantId: string, type: ShareableType, id: string, userId: string) =>
    api<{ level: TagLevel | 'none' | null }>(`${CRM}/${type}s/${id}/shares/reach/${userId}`, { tenantId }).then((body) => body.level),
  setRecordShares: (tenantId: string, type: ShareableType, id: string, userIds: string[]) =>
    api<{ shares: RecordShare[] }>(`${CRM}/${type}s/${id}/shares`, { method: 'PUT', tenantId, body: { userIds } }).then((body) => body.shares),
  /** The company group an account belongs to (TF-36): the tree and its totals per currency. */
  accountGroup: (tenantId: string, id: string) => api<AccountGroup>(`${CRM}/accounts/${id}/group`, { tenantId }),
  /** Where a file is linked (TF-25): what this person may see, and how many others. */
  fileLinks: (tenantId: string, fileId: string) => api<{ links: FileLink[]; hidden: number }>(`${CRM}/files/${fileId}/links`, { tenantId }),
  /** The links of up to 200 files at once (TF-58); files without any are left out. */
  /** The files linked to an account or a deal, directly or through its tasks (TF-81). */
  attachmentFiles: (tenantId: string, record: { accountId: string } | { dealId: string }) =>
    api<{ fileIds: string[] }>(`${CRM}/attachments/files${q(record)}`, { tenantId }),
  /** The accounts or deals that have files, by part of their name (TF-81). */
  recordsWithFiles: (tenantId: string, kind: 'account' | 'deal', text: string) =>
    api<{ records: { id: string; label: string; hint: string | null }[] }>(`${CRM}/attachments/records${q({ kind, q: text })}`, { tenantId }),
  /** The accounts tree beside Files (TF-365): accounts with files, a page at a time; with accountId, its deals with files. */
  fileTree: (tenantId: string, query: { accountId?: string; cursor?: string | null }) =>
    api<{ records: FileTreeRecord[]; nextCursor: string | null }>(`${CRM}/attachments/tree${q(query)}`, { tenantId }),
  filesLinks: (tenantId: string, fileIds: string[]) => api<{ files: FileLinks[] }>(`${CRM}/attachments/links${q({ fileIds: fileIds.join(',') })}`, { tenantId }),
};

// --------------------------------------------------------------------------- Administration

/** The Marketplace (TF-171): the catalogue with the company's choices, a tool switched, interest said. */
export const marketplaceApi = {
  list: (tenantId: string) => api<Marketplace>('/api/tenant/v1/marketplace', { tenantId }),
  setEnabled: (tenantId: string, id: string, enabled: boolean) => api<Marketplace>(`/api/tenant/v1/marketplace/${id}`, { method: 'PUT', tenantId, body: { enabled } }),
  setInterested: (tenantId: string, id: string, interested: boolean) =>
    api<Marketplace>(`/api/tenant/v1/marketplace/${id}/interest`, { method: 'PUT', tenantId, body: { interested } }),
};

/** AI assistants (TF-315): the company's switches and connections, and each person's own. */
export const assistantsApi = {
  get: (tenantId: string) => api<AssistantSettings>('/api/tenant/v1/assistants', { tenantId }),
  set: (tenantId: string, changes: { enabled?: boolean; write?: boolean }) => api<AssistantSettings>('/api/tenant/v1/assistants', { method: 'PUT', tenantId, body: changes }),
  mine: (tenantId: string) => api<MyAssistants>('/api/tenant/v1/assistants/mine', { tenantId }),
  revoke: (tenantId: string, userId: string, clientId: string) =>
    api<void>(`/api/tenant/v1/assistants/connections/${userId}/${encodeURIComponent(clientId)}`, { method: 'DELETE', tenantId }),
};

export const integrationsApi = {
  providers: (tenantId: string) => api<{ providers: Provider[] }>('/api/integrations/v1/providers', { tenantId }).then((body) => body.providers),
  connections: (tenantId: string) =>
    api<{ connections: Connection[] }>('/api/integrations/v1/connections', { tenantId }).then((body) => body.connections),
  /** OAuth: the answer is the provider's sign-in page; the browser goes there and comes back to /?integrations=… */
  startOAuth: (tenantId: string, provider: ProviderId) =>
    api<{ authorizeUrl: string }>(`/api/integrations/v1/connections/${provider}/start`, { method: 'POST', tenantId }),
  connectDokobit: (tenantId: string, apiToken: string, environment: string) =>
    api<Connection>('/api/integrations/v1/connections/dokobit', { method: 'POST', tenantId, body: { apiToken, environment } }),
  disconnect: (tenantId: string, id: string) => api<void>(`/api/integrations/v1/connections/${id}`, { method: 'DELETE', tenantId }),
  /** What is in a folder of a connected drive (the root when none is named). */
  driveItems: (tenantId: string, provider: string, folderId: string | null) =>
    api<{ items: DriveItem[] }>(`/api/integrations/v1/connections/${provider}/drive/items${q({ folderId })}`, { tenantId }).then((body) => body.items),
  /** Copies chosen files into a folder of this company; the answer is a receipt to follow. */
  importFromDrive: (tenantId: string, provider: string, itemIds: string[], folderId: string | null) =>
    api<DriveImport>(`/api/integrations/v1/connections/${provider}/drive/import`, { method: 'POST', tenantId, body: { itemIds, folderId } }),
  importStatus: (tenantId: string, id: string) => api<DriveImport>(`/api/integrations/v1/imports/${id}`, { tenantId }),
  /** Folders kept in step with a connected drive, one way out (ADR 0026). */
  mirrors: (tenantId: string) =>
    api<{ mirrors: DriveMirror[] }>('/api/integrations/v1/mirrors', { tenantId }).then((body) => body.mirrors),
  keepInDrive: (tenantId: string, provider: string, folderId: string, destinationId: string | null, destinationName: string | null) =>
    api<DriveMirror>('/api/integrations/v1/mirrors', { method: 'POST', tenantId, body: { provider, folderId, destinationId, destinationName } }),
  stopKeeping: (tenantId: string, id: string) => api<void>(`/api/integrations/v1/mirrors/${id}`, { method: 'DELETE', tenantId }),
  /** My Microsoft 365 or Gmail mailbox, read by itself (TF-323, TF-324): its state and the lists of addresses never to log. */
  mailLog: (tenantId: string) => api<MailLogSettings>('/api/integrations/v1/mail-log', { tenantId }),
  /** The labels of my Gmail I may choose, and those chosen (TF-324). */
  gmailLabels: (tenantId: string) => api<{ labels: GmailLabel[]; chosen: string[] }>('/api/integrations/v1/mail-log/labels', { tenantId }),
  chooseGmailLabels: (tenantId: string, labels: string[]) =>
    api<MailLogSettings>('/api/integrations/v1/mail-log/labels', { method: 'PUT', tenantId, body: { labels } }),
  /** My Google or Microsoft 365 calendar, kept in step with my meetings (TF-325). */
  calendarSync: (tenantId: string) => api<CalendarSyncSettings>('/api/integrations/v1/calendar-sync', { tenantId }),
  calendarChoices: (tenantId: string) =>
    api<{ calendars: CalendarChoice[] }>('/api/integrations/v1/calendar-sync/calendars', { tenantId }).then((body) => body.calendars),
  chooseCalendar: (tenantId: string, calendarId: string | null, name: string | null) =>
    api<CalendarSyncSettings>('/api/integrations/v1/calendar-sync/calendar', { method: 'PUT', tenantId, body: { calendarId, name } }),
  calendarSyncNow: (tenantId: string) => api<{ dueNow: boolean }>('/api/integrations/v1/calendar-sync/run', { method: 'POST', tenantId }),
  /** My busy times in a range (at most 63 days), for the DoCuLat calendar. */
  calendarBusy: (tenantId: string, from: string, to: string) =>
    api<{ busy: BusyTime[] }>(`/api/integrations/v1/calendar-sync/busy${q({ from, to })}`, { tenantId }).then((body) => body.busy),
  mailLogStart: (tenantId: string, startDays: 0 | 30 | 90) =>
    api<MailLogSettings>('/api/integrations/v1/mail-log/preferences', { method: 'PUT', tenantId, body: { startDays } }),
  mailLogExclusions: (tenantId: string, values: string[]) =>
    api<MailLogSettings>('/api/integrations/v1/mail-log/exclusions', { method: 'PUT', tenantId, body: { values } }),
  /** The company's list, for everybody: owners and admins. */
  mailLogCompanyExclusions: (tenantId: string, values: string[]) =>
    api<MailLogSettings>('/api/integrations/v1/mail-log/company-exclusions', { method: 'PUT', tenantId, body: { values } }),
  mailLogReadNow: (tenantId: string) => api<{ dueNow: boolean }>('/api/integrations/v1/mail-log/read', { method: 'POST', tenantId }),
  /** Look now, rather than when the next change or the next interval says so. */
  sweepNow: (tenantId: string, id: string) => api<{ dueNow: boolean }>(`/api/integrations/v1/mirrors/${id}/sweep`, { method: 'POST', tenantId }),
  /** The other direction: one file of this company, put into the connected drive. */
  exportToDrive: (tenantId: string, provider: string, fileId: string) =>
    api<{ itemId: string; name: string }>(`/api/integrations/v1/connections/${provider}/drive/export`, {
      method: 'POST',
      tenantId,
      body: { fileId },
    }),
};

// What is sent for signature is the shared rule's (TF-279), so the web app and the phone send the same.
export type { EnvelopeInput } from '@smbsuite/api/signing';

export const signingApi = {
  envelopes: (tenantId: string, filter: { fileId?: string; dealId?: string } = {}) =>
    api<{ envelopes: Envelope[] }>(`/api/signing/v1/envelopes${q(filter)}`, { tenantId }).then((body) => body.envelopes),
  envelope: (tenantId: string, id: string) => api<Envelope>(`/api/signing/v1/envelopes/${id}`, { tenantId }),
  send: (tenantId: string, input: EnvelopeInput) => api<Envelope>('/api/signing/v1/envelopes', { method: 'POST', tenantId, body: input }),
  cancel: (tenantId: string, id: string) => api<Envelope>(`/api/signing/v1/envelopes/${id}/cancel`, { method: 'POST', tenantId }),
};

export interface RuleInput {
  name: string;
  trigger: Trigger;
  actions: RuleAction[];
  maxRunsPerHour?: number;
}

export const automationApi = {
  catalog: (tenantId: string) => api<Catalog>('/api/automation/v1/catalog', { tenantId }),
  rules: (tenantId: string) => api<{ rules: Rule[] }>('/api/automation/v1/rules', { tenantId }).then((body) => body.rules),
  rule: (tenantId: string, id: string) => api<Rule>(`/api/automation/v1/rules/${id}`, { tenantId }),
  create: (tenantId: string, input: RuleInput) => api<Rule>('/api/automation/v1/rules', { method: 'POST', tenantId, body: input }),
  update: (tenantId: string, id: string, patch: Partial<RuleInput> & { enabled?: boolean }) =>
    api<Rule>(`/api/automation/v1/rules/${id}`, { method: 'PATCH', tenantId, body: patch }),
  remove: (tenantId: string, id: string) => api<void>(`/api/automation/v1/rules/${id}`, { method: 'DELETE', tenantId }),
  runs: (tenantId: string, ruleId?: string, limit = 30) =>
    api<{ runs: Run[] }>(ruleId ? `/api/automation/v1/rules/${ruleId}/runs${q({ limit })}` : `/api/automation/v1/runs${q({ limit })}`, { tenantId }).then(
      (body) => body.runs,
    ),
};

export interface AuditFilter {
  type?: string;
  actorId?: string;
  from?: string;
  to?: string;
}

export const auditApi = {
  events: (tenantId: string, filter: AuditFilter, cursor?: string | null) =>
    api<Page<AuditEvent>>(`/api/audit/v1/events${q({ ...filter, cursor, limit: 50 })}`, { tenantId }),
};

// What the editor can open (TF-168) is in libs/api, as the phone app reads it too (TF-274).
export type { EditorCapabilities } from './types';

export interface EditorSession {
  fileId: string;
  fileName: string;
  mode: 'edit' | 'view' | null;
  /**
   * Why somebody who may change the file has it read-only: the company is read-only, its plan has no
   * editing, or the plan sells editing per person and theirs is not switched on (TF-170).
   */
  readOnlyReason?: 'company' | 'plan' | 'person' | null;
  /** Why there is no editor page (TF-175): it is down, or at its limit of documents open at once. */
  unavailable?: 'editor' | 'busy' | null;
  /** The editor page; null when no editor can open this file right now. */
  url: string | null;
  accessToken: string;
  accessTokenTtl: number;
}

export const editorApi = {
  capabilities: (tenantId: string) => api<EditorCapabilities>('/api/wopi/v1/capabilities', { tenantId }),
  session: (tenantId: string, fileId: string, language: string) =>
    api<EditorSession>('/api/wopi/v1/sessions', { method: 'POST', tenantId, body: { fileId, language } }),
};

export const billingApi = {
  subscription: (tenantId: string) => api<Subscription>('/api/billing/v1/subscription', { tenantId }),
  plans: (tenantId: string) => api<{ currency: string; plans: Plan[]; current: Plan | null }>('/api/billing/v1/plans', { tenantId }),
  /** Owners only. Refused (409) when the company already uses more than the new plan allows. */
  /** Owners only. A paid plan with no card comes back as { needsCard, checkoutUrl } and no change. */
  changePlan: (tenantId: string, plan: string) =>
    api<Entitlements & { needsCard?: boolean; checkoutUrl?: string }>('/api/billing/v1/subscription', { method: 'POST', tenantId, body: { plan } }),
  /** Owners only: where to go and enter a card. Replaces the card that is there. */
  addCard: (tenantId: string) => api<{ checkoutUrl: string }>('/api/billing/v1/card', { method: 'POST', tenantId }),
  /** What the company's invoices must say about it, and whether this installation charges VAT. */
  details: (tenantId: string) => api<{ details: BillingDetails } & VatState>('/api/billing/v1/details', { tenantId }),
  /** The company's country alone, for everybody in it: numbers without a country code are dialled with it (TF-34). */
  country: (tenantId: string) => api<{ country: string | null }>('/api/billing/v1/country', { tenantId }).then((body) => body.country),
  cancel: (tenantId: string) => api<Entitlements & { endsAt: string }>('/api/billing/v1/subscription/cancel', { method: 'POST', tenantId }),
  invoices: (tenantId: string) => api<{ invoices: Invoice[] }>('/api/billing/v1/invoices', { tenantId }).then((body) => body.invoices),
  invoice: (tenantId: string, id: string) => api<Invoice>(`/api/billing/v1/invoices/${id}`, { tenantId }),
  /** The invoice as a PDF, rendered when it is asked for. */
  invoicePdf: (tenantId: string, id: string) => apiDownload(`/api/billing/v1/invoices/${id}/pdf`, { tenantId }),
};

export const companyPrivacyApi = {
  exports: (tenantId: string) => api<{ exports: ExportJob[] }>('/api/privacy/v1/exports', { tenantId }).then((body) => body.exports),
  startExport: (tenantId: string) => api<ExportJob>('/api/privacy/v1/exports', { method: 'POST', tenantId }),
  exportDownload: (tenantId: string, id: string) =>
    api<{ url: string; fileName: string; expiresAt: string }>(`/api/privacy/v1/exports/${id}/download`, { tenantId }),
  deletion: (tenantId: string) =>
    api<{ deletion: ErasureRequest | null }>('/api/privacy/v1/company/deletion', { tenantId }).then((body) => body.deletion),
  /** Owners only: the company closes now and is erased after the grace period. */
  scheduleDeletion: (tenantId: string, confirm: string) =>
    api<ErasureRequest>('/api/privacy/v1/company/deletion', { method: 'POST', tenantId, body: { confirm } }),
  cancelDeletion: (tenantId: string) => api<void>('/api/privacy/v1/company/deletion', { method: 'DELETE', tenantId }),
};
